import { ProtocolConsumption } from "../models/Entities/Protocols/ProtocolConsumption";
import { IResult } from "../models/HttpResult";
import { ErrorType } from "../models/HttpError";

// Functions
import * as Functions from '../functions/common';

// models
import { ProtocolConsumptionTotal } from "../models/Entities/Protocols/ProtocolConsumptionTotal";
import { NewProtocolConsumption } from "../models/Entities/Protocols/NewProtocolConsumption";
import { VisitTypeEnum } from "../models/Enums/VisitTypeEnum";
import { IPagedResult } from "../models/HttpPagedResult";
import { IConsumptionsFilters } from "../models/Filters/ConsumptionsFilters";

// Base URL
const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'protocols/protocols/';

/**
 * El gateway responde 200 aunque la operación no se haya podido hacer: el motivo
 * viaja en el cuerpo, como `succeeded: false` con un `message`. Mirar sólo
 * `response.ok` tomaba esos rechazos como éxito, y la pantalla no mostraba nada
 * —por ejemplo al agrupar un consumo sin concepto de contrato asociado—.
 *
 * El envelope es siempre el mismo, así que el chequeo va en todas las funciones
 * del archivo. Cuando el servidor rechaza, la acción deja de aplicarse y el
 * middleware despacha su FAILURE: es la pantalla la que tiene que mostrar el
 * mensaje, no el servicio el que lo trague.
 */
const unwrapResult = <T>(data: IResult<T>): T => {
    if (data && data.succeeded === false) {
        const error: ErrorType = {
            ErrorCode: data.statusCode ?? 400,
            ErrorMessage: data.message || 'No se pudo completar la operación',
            Errors: data.errors ?? []
        };

        throw error;
    }

    return data.data;
};

/**
 * Normaliza el rechazo al ErrorType de la app. Los que ya vienen normalizados
 * —los que lanza `unwrapResult`— no son una Response y se dejan pasar tal cual;
 * sin este chequeo, llamarles `.text()` los convertía en un TypeError y se
 * perdía el mensaje real del servidor.
 */
const handleRejection = async (error: any): Promise<never> => {
    if (typeof error?.text !== 'function') {
        throw error;
    }

    const body = await error.text();

    throw Functions.HttpErrorHandler(body, error);
};

// add method to get all protocol consumptions
export const GetAllProtocolConsumptionByProtocol = async (
    protocolId: number,
    filters: IConsumptionsFilters,
    pageNumber: number,
    pageSize: number,
    token: string
): Promise<IPagedResult<ProtocolConsumption[]>> => {

    let params: URLSearchParams = Functions.GetQueryStringParams(
        pageNumber,
        pageSize,
        filters.FilterText,
        filters
    );

    let RequestURL: string = BaseURL + protocolId + '/consumptions';

    if (params) RequestURL += '?' + params.toString();

    try {
        const Response: IPagedResult<ProtocolConsumption[]> = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IPagedResult<ProtocolConsumption[]>>;
            }
        })
        .then((data: IPagedResult<ProtocolConsumption[]>) => {
            return data;
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

export const GetConsumptionsByPatient = async (
    protocolId: number,
    numberInProtocol: string,
    token: string
): Promise<ProtocolConsumption[]> => {
    const RequestURL: string = BaseURL + protocolId + '/consumptions/patient/' + encodeURIComponent(numberInProtocol);
    try {
        const Response: ProtocolConsumption[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json();
            }
        })
        .then((data: any) => {
            return Array.isArray(data) ? data : (data?.data ?? []);
        })
        .catch(handleRejection);
        return Response;
    } catch (error: any) {
        throw error;
    }
};

// get the consumptions billed/included within the given consumption contract concept
export const GetConsumptionsBilledInConcept = async (
    token: string,
    consumptionId: string
): Promise<ProtocolConsumption[]> => {

    /*
    example url
    -> protocols/protocols/consumptions/3fa85f64-5717-4562-b3fc-2c963f66afa6/billed-in-concept
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/billed-in-concept';
    try {
        const Response: ProtocolConsumption[] = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json();
            }
        })
        .then((data: any) => {
            // El endpoint responde a veces con el envelope {data} y a veces
            // con el array pelado, igual que GetConsumptionsByPatient.
            return Array.isArray(data) ? data : (data?.data ?? []);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

export const GetAllProtocolConsumptionByStatus = async (
    protocolId: number,
    status: number,
    token: string)
    : Promise<ProtocolConsumption[]> => {	
    let RequestURL: string = BaseURL + protocolId + '/consumptions/status/' + status;
    try {
        const Response: ProtocolConsumption[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption[]>>;
            }
        })
        .then((data: IResult<ProtocolConsumption[]>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

export const GetAllProtocolConsumptionByProvider = async (
    providerId: string,
    token: string
): Promise<ProtocolConsumption[]> => {

    // https://prebiller.hc.alpha-cr.com/apis/prebiller-gateway/protocols/protocols/consumptions/provider/f0c90773-eacf-40fe-9643-e7639cd9b2d5

    let RequestURL: string = BaseURL + 'consumptions/provider/' + providerId;
    try {
        const Response: ProtocolConsumption[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption[]>>;
            }
        })
        .then((data: IResult<ProtocolConsumption[]>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// get protocol consumption by id
export const GetProtocolConsumptionById = async (
    token: string,
    id: string
): Promise<ProtocolConsumption> => {
    let RequestURL: string = BaseURL + 'consumptions/' + id;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// get totals by status
export const GetProtocolConsumptionTotals = async (
    protocolId: number,
    status: number,
    token: string
): Promise<ProtocolConsumptionTotal[]> => {
    let RequestURL: string = BaseURL + protocolId + '/consumptions/totals?status=' + status;
    try {
        const Response: ProtocolConsumptionTotal[] = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumptionTotal[]>>;
            }
        })
        // La anotación decía ProtocolConsumptionTotals[], distinto de lo que
        // declara el cast de arriba y de lo que devuelve la función. Antes pasaba
        // desapercibido porque el catch sin tipar ensanchaba la cadena a `any`.
        .then((data: IResult<ProtocolConsumptionTotal[]>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// create protocol consumption
export const CreateProtocolConsumption = async (
    token: string,
    protocolConsumption: NewProtocolConsumption
): Promise<ProtocolConsumption> => {
    let RequestURL: string = BaseURL + 'consumptions/';
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'POST',
        body: JSON.stringify(protocolConsumption)
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// update protocol consumption
export const UpdateProtocolConsumption = async (
    token: string,
    protocolConsumption: ProtocolConsumption
): Promise<ProtocolConsumption> => {
    let RequestURL: string = BaseURL + '/consumptions/';
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PUT',
        body: JSON.stringify(protocolConsumption)
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// delete protocol consumption
export const DeleteProtocolConsumption = async (
    token: string,
    id: string
): Promise<ProtocolConsumption> => {
    let RequestURL: string = BaseURL + 'consumptions/' + id;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'DELETE'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// mark protocol consumption as follow-up
export const MarkConsumptionFollowUp = async (
    token: string,
    consumptionId: string
): Promise<ProtocolConsumption> => {
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/follow-up';
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

/**
 * Discontinuación temprana. Son dos endpoints —marcar y revertir— sobre el
 * mismo dato: el `condition` del consumo pasa a EarlyDiscontinuation y vuelve.
 */
export const SetConsumptionEarlyDiscontinuation = async (
    token: string,
    consumptionId: string,
    isEarlyDiscontinuation: boolean
): Promise<ProtocolConsumption> => {

    /*
    example url
    -> protocols/protocols/consumptions/{id}/early-discontinuation
    -> protocols/protocols/consumptions/{id}/early-discontinuation/revert
    */
    const RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/early-discontinuation'
        + (isEarlyDiscontinuation ? '' : '/revert');

    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

/**
 * Agrupa o desagrupa el consumo dentro de su visita. El servidor usa la misma
 * mecánica que el proceso de sync: crea la cabecera por etapa y paciente si al
 * agrupar no existe, y la elimina si al desagrupar queda sin hijos.
 */
export const SetConsumptionGrouping = async (
    token: string,
    consumptionId: string,
    group: boolean
): Promise<ProtocolConsumption> => {

    /*
    example url
    -> protocols/protocols/consumptions/{id}/grouping?group=true
    */
    const RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/grouping?group=' + group;

    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// change protocol consumption status
export const ChangeProtocolConsumptionStatus = async (
    token: string,
    protocolId: number,
    consumptionId: string,
    status: number,
    applyToAll: boolean
): Promise<ProtocolConsumption> => {
    let RequestURL: string = BaseURL + 'consumptions/' + 
    consumptionId + '?newStatus=' + status + '&applyToAll=' + applyToAll;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// change protocol consumption concept

export const SetConsumptionConcept = async (
    token: string,
    consumptionId: string,
    conceptId: number,
    changeAll: boolean
): Promise<ProtocolConsumption[]> => {

    /*
    example url
    -> consumption/3fa85f64-5717-4562-b3fc-2c963f66afa6/concept/12?ChangeAll=false
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/concept/' + conceptId + '?ChangeAll=' + changeAll;
    try {
        const Response: ProtocolConsumption[] = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption[]>>;
            }
        })
        .then((data: IResult<ProtocolConsumption[]>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
}

// change protocol consumption producer 

export const SetConsumptionProducer = async (
    token: string,
    consumptionId: string,
    producerId: string
): Promise<ProtocolConsumption[]> => {

    /*
    example url
    -> consumption/3fa85f64-5717-4562-b3fc-2c963f66afa6/producer/12
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/producer/' + producerId;
    try {
        const Response: ProtocolConsumption[] = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption[]>>;
            }
        })
        .then((data: IResult<ProtocolConsumption[]>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
}

// change protocol consumption provider

export const SetConsumptionProvider = async (
    token: string,
    consumptionId: string,
    providerId: number
): Promise<ProtocolConsumption> => {

    /*
    example url
    -> consumption/3fa85f64-5717-4562-b3fc-2c963f66afa6/provider/12
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/provider/' + providerId;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
}

// change protocol consumption medical service

export const SetConsumptionMedicalService = async (
    token: string,
    consumptionId: string,
    serviceId: number
): Promise<ProtocolConsumption> => {

    /*
    example url
    -> consumption/3fa85f64-5717-4562-b3fc-2c963f66afa6/service/12
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/medicalService/' + serviceId;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
}

// change amount of protocol consumption
    
export const SetConsumptionAmount = async (
    token: string,
    consumptionId: string,
    amount: number,
    currencyCode: string
): Promise<ProtocolConsumption> => {

    /*
    example url
    -> consumption/3fa85f64-5717-4562-b3fc-2c963f66afa6/amount?newAmount=1500&currency=ARS
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/amount?newAmount=' + amount + '&currency=' + currencyCode;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// change visit type
export const SetConsumptionVisitType = async (
    token: string,
    consumptionId: string,
    visitTypeId: VisitTypeEnum
): Promise<ProtocolConsumption> => {

    /*
    example url
    -> protocols/consumptions/3fa85f64-5717-4562-b3fc-2c963f66afa6/type/1
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/type/' + visitTypeId;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// change visit (stage) of protocol consumption
export const SetConsumptionVisitStage = async (
    token: string,
    consumptionId: string,
    stageId: number
): Promise<ProtocolConsumption> => {

    /*
    example url
    -> protocols/consumptions/3fa85f64-5717-4562-b3fc-2c963f66afa6/visit/10
    */
    let RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/visit/' + stageId;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token
        },
        method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
            throw response;
            } else {
            return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// set as procedure
export const SetConsumptionAsProcedure = async (
    token: string,
    consumptionId: string,
    isProcedure: boolean,
    changeAll: boolean
): Promise<ProtocolConsumption> => {
    /*
    example url
    -> protocols/protocols/consumptions/{id}/procedure?isProcedure=true&changeAll=false
    */
    const RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/procedure?isProcedure=' + isProcedure + '&changeAll=' + changeAll;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// set as additional
export const SetConsumptionAsAdditional = async (
    token: string,
    consumptionId: string,
    isAdditional: boolean,
    changeAll: boolean
): Promise<ProtocolConsumption> => {
    /*
    example url
    -> protocols/protocols/consumptions/{id}/additional?isAdditional=true&changeAll=false
    */
    const RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/additional?isAdditional=' + isAdditional + '&changeAll=' + changeAll;
    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};

export const ExportAllProtocolConsumptionByProtocol = async (
    filters: IConsumptionsFilters,
    token: string,
    protocolId: string
): Promise<Blob> => {

    let params: URLSearchParams = Functions.GetQueryStringParams(
        undefined,
        undefined,
        filters.FilterText,
        filters
    );

    let RequestURL: string = BaseURL + protocolId + '/consumptions/export';

    if (params) RequestURL += '?' + params.toString();

    try {
        const Response: Blob = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'GET'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } 
            else {
                return response.blob() as Promise<Blob>;
            }
        })
        .then((data: Blob) => {
            return data;
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};
/**
 * Cambia la condición del consumo a cualquier valor del enum, incluida Normal
 * (PATCH protocols/consumptions/{id}/condition/{condition}). Reemplaza a los
 * endpoints puntuales de discontinuación, seguimiento y revertir.
 */
export const SetConsumptionCondition = async (
    token: string,
    consumptionId: string,
    condition: number
): Promise<ProtocolConsumption> => {
    const RequestURL: string = BaseURL + 'consumptions/' + consumptionId + '/condition/' + condition;

    try {
        const Response: ProtocolConsumption = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            method: 'PATCH'
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<ProtocolConsumption>>;
            }
        })
        .then((data: IResult<ProtocolConsumption>) => {
            return unwrapResult(data);
        })
        .catch(handleRejection);

        return Response;
    } catch (error: any) {
        throw error;
    }
};
